<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="modalTitle"
    :steps="wizardSteps"
    size="medium"
    :reset-on-close="true"
    :loading="isSaving"
    @cancel="handleClose"
    @finish="handleFinish"
    @close="handleClose"
  >
    <!-- Step 0: Basic Info -->
    <template #step-0>
      <div class="wizard-step-content">
        <!-- Franchise Selection -->
        <div class="form-row">
          <FranchiseSelect
            v-model="formData.franchise_id"
            elem-id="advance-franchise"
            :label="t('Franquia')"
            :disabled="isEditMode"
            @update:modelValue="handleFranchiseChange"
          />
        </div>

        <!-- Title -->
        <div class="form-row">
          <input-text
            v-model="formData.title"
            data-testid="advance-titulo-input"
            elem-id="advance-title"
            :label="t('Título')"
            :placeholder="t('Ex: Adiantamento salarial')"
            :required="true"
            :validation="() => formData.title && formData.title.trim() !== ''"
          />
        </div>

        <!-- Amount -->
        <div class="form-row">
          <InputCurrency
            v-model="formData.amount"
            elem-id="advance-amount"
            :label="t('Valor')"
            ng-required="true"
            :validation="() => formData.amount > 0"
          />
        </div>

        <!-- Request Date -->
        <div class="form-row">
          <InputDate
            v-model="formData.request_date"
            elem-id="advance-request-date"
            :label="t('Data da solicitação')"
            :ng-required="true"
            :validation="() => !!formData.request_date"
          />
        </div>
      </div>
    </template>

    <!-- Step 1: Employee Selection -->
    <template #step-1>
      <div class="wizard-step-content">
        <h3 class="step-title">{{ t('Selecione o colaborador') }}:</h3>

        <div v-if="loadingUsers" class="loading-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
          <p>{{ t('Carregando usuários...') }}</p>
        </div>

        <Message v-else-if="userOptions.length === 0" severity="warn">
          <template #icon>
            <i class="pi pi-exclamation-triangle"></i>
          </template>
          {{ t('Nenhum usuário disponível para esta franquia') }}
        </Message>

        <ButtonsList
          v-else
          :itens-select="userOptions"
          :value="formData.employee_id"
          @input="formData.employee_id = $event"
          elem-id="advance_employee"
          :search-enabled="userOptions.length > 5"
        />
      </div>
    </template>

    <!-- Step 2: Description -->
    <template #step-2>
      <div class="wizard-step-content">
        <!-- Description (Rich Text) -->
        <div class="form-row">
          <InputRichText
            v-model="formData.description"
            elem-id="advance-description"
            :label="t('Descrição')"
            :placeholder="t('Descreva o motivo do adiantamento (opcional)')"
            :show-toolbar="true"
            min-height="120px"
          />
        </div>

        <!-- Delete Button (only for edit mode with open status) -->
        <div v-if="isEditMode && advanceData?.status === 1" class="delete-section">
          <Button
            data-testid="advance-excluir-btn"
            :label="t('Excluir adiantamento')"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            @click="handleDelete"
          />
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { useEmployeeAdvances } from '../../../composables/useEmployeeAdvances';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { permissionsService } from '../../../services/permissionsService';

type Id = number | string;

interface AdvancePayload {
  id?: Id;
  franchise_id?: Id | null;
  employee_id?: Id | null;
  title?: string;
  amount?: number;
  request_date?: string | Date | null;
  request_date_string?: string;
  description?: string;
  status?: number;
  [key: string]: unknown;
}

interface FormData {
  franchise_id: Id | null;
  employee_id: Id | null;
  title: string;
  amount: number;
  request_date: string;
  description: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserOption = any;

const { t } = useI18n();
const { createAdvance, updateAdvance, deleteAdvance, getAdvance, error } = useEmployeeAdvances();
const { confirmAction } = useConfirmAction();
const { fromDateToApiDate } = useDateFormatter();

// State
const isVisible = ref<boolean>(false);
const advanceId = ref<Id | null>(null);
const advanceData = ref<AdvancePayload | null>(null);
const isSaving = ref<boolean>(false);
const loadingUsers = ref<boolean>(false);
const userOptions = ref<UserOption[]>([]);

// Form data
const formData = ref<FormData>({
  franchise_id: null,
  employee_id: null,
  title: '',
  amount: 0,
  request_date: '',
  description: ''
});

// Computed
const isEditMode = computed((): boolean => {
  return !!advanceId.value || !!advanceData.value?.id;
});

const modalTitle = computed((): string => {
  return isEditMode.value
    ? t('Editar Adiantamento')
    : t('Novo Adiantamento');
});

// Step validation functions
const validateStep0 = (): boolean => {
  return !!(formData.value.franchise_id &&
         formData.value.title &&
         formData.value.title.trim() !== '' &&
         formData.value.amount > 0 &&
         formData.value.request_date);
};

const validateStep1 = (): boolean => {
  return !!formData.value.employee_id;
};

const validateStep2 = (): boolean => {
  // Description is optional, always valid
  return true;
};

// Wizard steps configuration
const wizardSteps = computed(() => [
  {
    title: t('Informações'),
    description: t('Dados básicos do adiantamento'),
    validate: validateStep0
  },
  {
    title: t('Colaborador'),
    description: t('Quem receberá o adiantamento'),
    validate: validateStep1
  },
  {
    title: t('Descrição'),
    description: t('Detalhe adicionais'),
    validate: validateStep2
  }
]);

// Methods
const resetForm = (): void => {
  formData.value = {
    franchise_id: null,
    employee_id: null,
    title: '',
    amount: 0,
    request_date: '',
    description: ''
  };
  advanceId.value = null;
  advanceData.value = null;
  userOptions.value = [];
};

/**
 * Handle franchise selection change - load users for the selected franchise
 */
const handleFranchiseChange = (franchiseId: Id | null): void => {
  log('EmployeeAdvancesModal', 'handleFranchiseChange', 'Franchise changed', franchiseId);

  // Reset user selection when franchise changes
  formData.value.employee_id = null;

  if (franchiseId) {
    loadUsersForFranchise(franchiseId);
  } else {
    userOptions.value = [];
  }
};

/**
 * Load users for a specific franchise using permissionsService
 */
const loadUsersForFranchise = (franchiseId: Id): void => {
  loadingUsers.value = true;

  try {
    log('EmployeeAdvancesModal', 'loadUsersForFranchise', 'Loading users for franchise', franchiseId);

    // Get operadores (and all users) for the franchise
    const operadores = permissionsService.getOperadoresSelectArrayByFranquiaID(
      typeof franchiseId === 'string' ? parseInt(franchiseId) : franchiseId
    );

    userOptions.value = operadores;
    log('EmployeeAdvancesModal', 'loadUsersForFranchise', 'Loaded users', userOptions.value.length);
  } catch (err) {
    log('EmployeeAdvancesModal', 'loadUsersForFranchise', 'Error loading users', err);
    userOptions.value = [];
  } finally {
    loadingUsers.value = false;
  }
};

const handleModalOpen = async (data: AdvancePayload | null = null): Promise<void> => {
  log('EmployeeAdvancesModal', 'handleModalOpen', 'Opening modal', data);

  resetForm();

  if (data?.id) {
    // Edit mode - load from data or fetch
    advanceId.value = data.id;
    advanceData.value = data;

    // Check if we have enough data to populate the form
    const hasFullData = data.title || (data.franchise_id && data.amount);

    log('EmployeeAdvancesModal', 'handleModalOpen', 'Data check', {
      hasFullData,
      hasTitle: !!data.title,
      hasFranchiseAndAmount: !!(data.franchise_id && data.amount),
      dataKeys: Object.keys(data || {})
    });

    if (hasFullData) {
      // Use the provided data
      let requestDateStr = '';
      if (data.request_date_string) {
        requestDateStr = data.request_date_string;
      } else if (data.request_date instanceof Date) {
        requestDateStr = fromDateToApiDate(data.request_date);
      } else if (data.request_date) {
        requestDateStr = data.request_date as string;
      }

      formData.value = {
        franchise_id: (data.franchise_id as Id | null) ?? null,
        employee_id: (data.employee_id as Id | null) ?? null,
        title: (data.title as string) || '',
        amount: (data.amount as number) || 0,
        request_date: requestDateStr,
        description: (data.description as string) || ''
      };
      log('EmployeeAdvancesModal', 'handleModalOpen', 'Form data populated from provided data', formData.value);

      // Load users for the franchise if set
      if (data.franchise_id) {
        loadUsersForFranchise(data.franchise_id as Id);
      }
    } else {
      // Fetch full data from API
      log('EmployeeAdvancesModal', 'handleModalOpen', 'Fetching full advance data from API', data.id);
      const advance = await getAdvance(data.id);
      if (advance) {
        log('EmployeeAdvancesModal', 'handleModalOpen', 'Advance data fetched', advance);

        let requestDateStr = '';
        if (advance.request_date_string) {
          requestDateStr = advance.request_date_string;
        } else if (advance.request_date instanceof Date) {
          requestDateStr = fromDateToApiDate(advance.request_date);
        } else if (advance.request_date) {
          requestDateStr = advance.request_date;
        }

        formData.value = {
          franchise_id: advance.franchise_id ?? null,
          employee_id: advance.employee_id ?? null,
          title: advance.title || '',
          amount: advance.amount || 0,
          request_date: requestDateStr,
          description: advance.description || ''
        };
        // Load users for the franchise if set
        if (advance.franchise_id) {
          loadUsersForFranchise(advance.franchise_id);
        }
      } else {
        log('EmployeeAdvancesModal', 'handleModalOpen', 'Failed to fetch advance data', data.id);
      }
    }
  } else if (data) {
    // New with initial data (e.g., pre-filled franchise)
    formData.value.franchise_id = (data.franchise_id as Id | null) ?? null;
    if (data.franchise_id) {
      loadUsersForFranchise(data.franchise_id as Id);
    }
  }

  // Set default request date to today if not set
  if (!formData.value.request_date) {
    formData.value.request_date = fromDateToApiDate(new Date());
  }

  isVisible.value = true;
};

const handleClose = (): void => {
  log('EmployeeAdvancesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  resetForm();
};

const handleFinish = async (): Promise<void> => {
  log('EmployeeAdvancesModal', 'handleFinish', 'Finishing wizard', formData.value);

  isSaving.value = true;

  try {
    const payload = {
      title: formData.value.title,
      description: formData.value.description,
      amount: formData.value.amount,
      request_date: formData.value.request_date,
      franchise_id: formData.value.franchise_id,
      employee_id: formData.value.employee_id
    };

    let result: unknown;
    let errorMessage: string | null = null;

    if (isEditMode.value) {
      const id = advanceId.value || advanceData.value?.id;
      result = await updateAdvance(id, payload);
      if (!result) {
        errorMessage = (error.value as unknown as string) || t('Erro ao atualizar adiantamento');
      }
    } else {
      result = await createAdvance(payload);
      if (!result) {
        errorMessage = (error.value as unknown as string) || t('Erro ao criar adiantamento');
      }
    }

    if (result) {
      log('EmployeeAdvancesModal', 'handleFinish', 'Saved successfully', result);

      // Emit events
      eventBus.emit('employee-advances.created');
      eventBus.emit('employee-advances.updated');
      eventBus.emit('employee-advances.refresh');

      // Show success message
      eventBus.emit('globalMessage.success', isEditMode.value
        ? t('Adiantamento atualizado com sucesso')
        : t('Adiantamento criado com sucesso'));

      handleClose();
    } else {
      // Show error from backend
      log('EmployeeAdvancesModal', 'handleFinish', 'Error from backend', errorMessage);
      eventBus.emit('globalMessage.error', errorMessage);
    }
  } catch (err) {
    log('EmployeeAdvancesModal', 'handleFinish', 'Error', err);
    const message = err instanceof Error ? err.message : t('Erro ao salvar adiantamento');
    eventBus.emit('globalMessage.error', message);
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = (): void => {
  const id = advanceId.value || advanceData.value?.id;
  if (!id) return;

  log('EmployeeAdvancesModal', 'handleDelete', 'Deleting advance', id);

  confirmAction({
    message: t('Tem certeza que deseja excluir este adiantamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await deleteAdvance(id);
      if (success) {
        eventBus.emit('globalMessage.success', {
          message: t('Adiantamento excluído com sucesso'),
          duration: 3000
        });
        handleClose();
        eventBus.emit('employee-advances.refresh');
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao excluir adiantamento'),
          duration: 5000
        });
      }
    }
  });
};

// Store handler ref to ensure matching on/off — fixes latent bug where anonymous arrow
// was registered in onMounted but could not be deregistered in onBeforeUnmount
const handleOpenModalNew = (): void => { handleModalOpen(null); };

// Event listeners
onMounted(() => {
  // Standard modal events
  eventBus.on('modal.employee-advances.open', handleModalOpen);
  eventBus.on('modal.employee-advances.close', handleClose);

  // Legacy/alternative events
  eventBus.on('employee-advances.open-modal-new', handleOpenModalNew);
  eventBus.on('employee-advances.open-modal-edit', handleModalOpen);
  eventBus.on('employee-advances.edit', handleModalOpen);
});

onBeforeUnmount(() => {
  // Standard modal events
  eventBus.off('modal.employee-advances.open', handleModalOpen);
  eventBus.off('modal.employee-advances.close', handleClose);

  // Legacy/alternative events
  eventBus.off('employee-advances.open-modal-new', handleOpenModalNew);
  eventBus.off('employee-advances.open-modal-edit', handleModalOpen);
  eventBus.off('employee-advances.edit', handleModalOpen);
});
</script>

<style scoped>
.wizard-step-content {
  padding: 0.5rem;
}

.step-title {
  margin-bottom: 1rem;
  font-weight: 500;
  color: var(--text-color);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.form-row {
  margin-bottom: 1.25rem;
}

.delete-section {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);
  display: flex;
  justify-content: flex-end;
}
</style>
